/**
 * @fileoverview Initial catalog load, navigation setup, and current-user resolution.
 * @module hooks/companyCatalog/useCompanyCatalogInit
 */
import { useCallback, useEffect, useState } from "react";
import { getData } from "../../logic/companyCatalog/index.js";
import { getUser } from "../../logic/user/index.js";
import { logger, sanitizeError } from "../../utils/logger.js";

const COMPANY_CATALOG_PATH = "/companyCatalog";

/**
 * Loads the first catalog page, wires navigation context, and resolves whether
 * the signed-in collaborator can manage create-requirements permissions.
 *
 * @param {Object} options Hook dependencies from the parent catalog composer
 * @param {import('react-router').NavigateFunction} options.navigate Router navigate for catalog APIs
 * @param {Function} options.changePermissions AppContext permission updater passed to getData
 * @param {Function} options.updateNavigation NavigationContext updater for active route metadata
 * @param {(items: Array<Object>) => void} options.setInitialCompanies Seeds search hook state with first page
 * @param {import('react').Dispatch<import('react').SetStateAction<string|null>>} options.setLastKey Stores DynamoDB pagination cursor
 * @returns {{
 *   initialLoading: boolean,
 *   showCreateRequirementsColumn: boolean
 * }} Initial load flag and column visibility derived from the current user
 */
export const useCompanyCatalogInit = ({
  navigate,
  changePermissions,
  updateNavigation,
  setInitialCompanies,
  setLastKey,
}) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const handleGetData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const result = await getData(changePermissions, navigate);
      setInitialCompanies(result.items);
      setLastKey(result.lastKey);
      const userId = localStorage.getItem("userId");
      if (userId) {
        const user = await getUser(userId, navigate);
        setCurrentUser(user);
      }
    } catch (error) {
      logger.error("Error in handleGetData:", { err: sanitizeError(error) });
    } finally {
      setInitialLoading(false);
    }
  }, [changePermissions, navigate, setInitialCompanies, setLastKey]);

  useEffect(() => {
    updateNavigation(COMPANY_CATALOG_PATH, "companyCatalog");

    const token = localStorage.getItem("token");
    if (token && token.trim() !== "undefined") {
      handleGetData();
    } else {
      logger.error(
        "Error trying to get the token to retrieve the companies data.",
      );
      setInitialLoading(false);
    }
  }, [handleGetData, updateNavigation]);

  const showCreateRequirementsColumn = currentUser?.esColaborador === true;

  return {
    initialLoading,
    showCreateRequirementsColumn,
  };
};
